import React, { useState, useEffect, useCallback } from 'react';
import { useSession } from '../lib/auth';
import { useNavigate } from 'react-router-dom';
import { 
  User, 
  Calendar, 
  Clock, 
  MapPin, 
  Users, 
  Mail, 
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader,
  Play,
  Pause
} from 'lucide-react';
import Navigation from '../components/Navigation';
import { API_ENDPOINTS } from '../config/api';
import Toast from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';

interface Course {
  id: number;
  name: string;
  location: string;
}

interface MonitoringRequest {
  id: number;
  course_ids: number[];
  preferred_dates: string[];
  start_time: string;
  end_time: string;
  number_of_players: number;
  contact_phone?: string;
  contact_email: string;
  notification_method: 'email' | 'sms' | 'both';
  is_active: boolean;
  created_at: string;
  expires_at: string;
  courses: Course[];
}


function UserDashboard() {
  const { data: session, isPending } = useSession();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<MonitoringRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [toast, setToast] = useState<{
    type: 'success' | 'error';
    title: string;
    message: string;
    isVisible: boolean;
  }>({ type: 'success', title: '', message: '', isVisible: false });
  const [confirmDialog, setConfirmDialog] = useState<{
    isVisible: boolean;
    requestId: number | null;
    currentStatus: boolean;
    action: string;
  }>({ isVisible: false, requestId: null, currentStatus: false, action: '' });
  const [activeTab, setActiveTab] = useState<'active' | 'previous'>('active');

  const fetchUserRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Better-auth session 验证
      
      if (!session?.session) {
        throw new Error('No valid session found');
      }

      // Better-auth automatically handles cookies, no need for manual Authorization header
      const response = await fetch(API_ENDPOINTS.getUserRequests, {
        credentials: 'include', // Important: include cookies
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch monitoring requests');
      }

      const data = await response.json();
      if (data.success) {
        setRequests(data.data);
      } else {
        throw new Error(data.error || 'Failed to fetch requests');
      }
    } catch (err: any) {
      console.error('Error fetching user requests:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [session]);


  useEffect(() => {
    if (!isPending && !session?.user) {
      navigate('/waitlist');
      return;
    }

    if (session?.user) {
      fetchUserRequests();
    }
  }, [session, isPending, navigate, fetchUserRequests]);

  const handleToggleRequest = (requestId: number, currentStatus: boolean) => {
    const action = currentStatus ? 'deactivate' : 'reactivate';
    
    // 如果是重新启动，直接执行，不需要确认
    if (!currentStatus) {
      confirmToggleRequest(requestId, currentStatus, action);
      return;
    }
    
    // 只有暂停时才需要确认
    setConfirmDialog({
      isVisible: true,
      requestId,
      currentStatus,
      action
    });
  };

  const confirmToggleRequest = async (reqId?: number, currStatus?: boolean, actionType?: string) => {
    // 从参数或confirmDialog状态获取值
    const requestId = reqId !== undefined ? reqId : confirmDialog.requestId;
    const currentStatus = currStatus !== undefined ? currStatus : confirmDialog.currentStatus;
    const action = actionType || confirmDialog.action;
    
    if (!requestId || requestId === null) {
      return;
    }
    
    setConfirmDialog({ isVisible: false, requestId: null, currentStatus: false, action: '' });

    try {
      setCancellingId(requestId);

      const response = await fetch(API_ENDPOINTS.cancelUserRequest(requestId), {
        method: 'PATCH',
        credentials: 'include', // Important: include cookies
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !currentStatus })
      });

      if (!response.ok) {
        throw new Error(`Failed to ${action} request`);
      }

      const data = await response.json();
      if (data.success) {
        // 更新本地状态
        setRequests(prev => prev.map(req => 
          req.id === requestId ? { ...req, is_active: !currentStatus } : req
        ));
        setToast({
          type: 'success',
          title: `Waitlist ${action}d successfully`,
          message: `Your waitlist request has been ${action}d.`,
          isVisible: true
        });
      } else {
        throw new Error(data.error || `Failed to ${action} request`);
      }
    } catch (err: any) {
      console.error(`Error ${action}ing request:`, err);
      setToast({
        type: 'error',
        title: `Failed to ${action} request`,
        message: err.message || `An error occurred while trying to ${action} the waitlist request.`,
        isVisible: true
      });
    } finally {
      setCancellingId(null);
    }
  };


  if (isPending || loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center py-12">
            <Loader className="animate-spin mr-3" size={24} />
            <span className="text-lg text-gray-600">Loading your dashboard...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!session?.user) {
    return null; // Will redirect
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertCircle className="mx-auto mb-4 text-red-500" size={48} />
            <h2 className="text-xl font-semibold text-red-800 mb-2">Error Loading Dashboard</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <button 
              onClick={fetchUserRequests}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const activeRequests = requests.filter(req => req.is_active);
  const inactiveRequests = requests.filter(req => !req.is_active);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* User Info Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center">
                {session.user.image ? (
                  <img 
                    src={session.user.image} 
                    alt={session.user.name || 'User'} 
                    className="w-16 h-16 rounded-full object-cover"
                  />
                ) : (
                  <User className="text-white" size={32} />
                )}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Welcome, {session.user.name || 'User'}!
                </h1>
                <p className="text-gray-600">{session.user.email}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="grid grid-cols-2 gap-8">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="text-green-600" size={20} />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Active</p>
                <p className="text-xl font-bold text-gray-900">{activeRequests.length}</p>
              </div>
            </div>
            
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="text-blue-600" size={20} />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">All Time</p>
                <p className="text-xl font-bold text-gray-900">{requests.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs for Waitlists */}
        {requests.length > 0 && (
          <div className="mb-8">
            {/* Tab Navigation */}
            <div className="border-b border-gray-200 mb-6">
              <nav className="flex space-x-8">
                <button
                  onClick={() => setActiveTab('active')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'active'
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center">
                    <CheckCircle className="mr-2" size={16} />
                    Active ({activeRequests.length})
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('previous')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'previous'
                      ? 'border-gray-500 text-gray-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center">
                    <AlertCircle className="mr-2" size={16} />
                    Previous ({inactiveRequests.length})
                  </div>
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            <div className="space-y-4">
              {activeTab === 'active' && activeRequests.length > 0 && (
                <>
                  {activeRequests.map((request) => (
                    <RequestCard 
                      key={request.id} 
                      request={request} 
                      onToggle={handleToggleRequest}
                      cancellingId={cancellingId}
                    />
                  ))}
                </>
              )}
              
              {activeTab === 'active' && activeRequests.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="mx-auto mb-4" size={48} />
                  <p>No active waitlists</p>
                </div>
              )}
              
              {activeTab === 'previous' && inactiveRequests.length > 0 && (
                <>
                  {inactiveRequests.map((request) => (
                    <RequestCard 
                      key={request.id} 
                      request={request} 
                      onToggle={handleToggleRequest}
                      cancellingId={cancellingId}
                    />
                  ))}
                </>
              )}
              
              {activeTab === 'previous' && inactiveRequests.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <AlertCircle className="mx-auto mb-4" size={48} />
                  <p>No previous waitlists</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Empty State */}
        {requests.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Calendar className="mx-auto mb-4 text-gray-400" size={64} />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Ready to Play Golf?</h3>
            <p className="text-gray-600 mb-6">Join waitlists for your favorite golf courses and get notified when tee times become available.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => window.open('http://localhost:3001/tee-times', '_blank')}
                className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
              >
                Find Tee Times
              </button>
              <button
                onClick={() => navigate('/waitlist')}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Join Waitlist
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Toast Notification */}
      <Toast
        type={toast.type}
        title={toast.title}
        message={toast.message}
        isVisible={toast.isVisible}
        onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
      />
      
      {/* Confirm Dialog */}
      <ConfirmDialog
        isVisible={confirmDialog.isVisible}
        onConfirm={() => confirmToggleRequest()}
        onCancel={() => setConfirmDialog({ isVisible: false, requestId: null, currentStatus: false, action: '' })}
        title={`${confirmDialog.action === 'deactivate' ? 'Pause' : 'Activate'} Waitlist`}
        message={`Are you sure you want to ${confirmDialog.action} this waitlist request?`}
        confirmText={confirmDialog.action === 'deactivate' ? 'Pause' : 'Activate'}
        cancelText="Cancel"
        type={confirmDialog.action === 'deactivate' ? 'warning' : 'info'}
        icon={confirmDialog.action === 'deactivate' ? <Pause className="text-orange-600 mr-3" size={24} /> : <Play className="text-blue-600 mr-3" size={24} />}
      />
    </div>
  );
}

interface RequestCardProps {
  request: MonitoringRequest;
  onToggle: (id: number, currentStatus: boolean) => void;
  cancellingId: number | null;
}

function RequestCard({ request, onToggle, cancellingId }: RequestCardProps) {
  const formatTime = (timeString: string) => {
    const date = new Date(timeString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getRequestStatus = (request: MonitoringRequest) => {
    const now = new Date();
    const expiresAt = new Date(request.expires_at);
    
    if (!request.is_active) {
      return { status: 'cancelled', color: 'text-red-600', icon: XCircle };
    }
    
    if (now > expiresAt) {
      return { status: 'expired', color: 'text-gray-600', icon: AlertCircle };
    }
    
    return { status: 'active', color: 'text-green-600', icon: CheckCircle };
  };

  const { status, color, icon: StatusIcon } = getRequestStatus(request);
  const isCancelling = cancellingId === request.id;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <StatusIcon className={`${color}`} size={20} />
          <div>
            <h3 className="font-semibold text-gray-900">
              Waitlist Request #{request.id}
            </h3>
            <p className="text-sm text-gray-500">
              Created {new Date(request.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
            status === 'active' 
              ? 'bg-green-100 text-green-800' 
              : status === 'expired'
              ? 'bg-gray-100 text-gray-800'
              : 'bg-red-100 text-red-800'
          }`}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
          
          <button
            onClick={() => onToggle(request.id, request.is_active)}
            disabled={isCancelling}
            className={`p-1 rounded disabled:opacity-50 ${
              request.is_active 
                ? 'text-orange-600 hover:bg-orange-50' 
                : 'text-green-600 hover:bg-green-50'
            }`}
            title={request.is_active ? 'Deactivate waitlist' : 'Reactivate waitlist'}
          >
            {isCancelling ? (
              <Loader className="animate-spin" size={16} />
            ) : request.is_active ? (
              <Pause size={16} />
            ) : (
              <Play size={16} />
            )}
          </button>
        </div>
      </div>

      {/* Course Information */}
      <div className="space-y-3">
        <div className="flex items-center text-sm text-gray-600">
          <MapPin className="mr-2" size={16} />
          <span className="font-medium">Golf Courses:</span>
          <span className="ml-2">
            {request.courses.map(course => course.name).join(', ')}
          </span>
        </div>

        <div className="flex items-center text-sm text-gray-600">
          <Calendar className="mr-2" size={16} />
          <span className="font-medium">Preferred Dates:</span>
          <span className="ml-2">
            {request.preferred_dates.map(date => {
              // 使用 UTC 方法确保显示正确的日期，不受时区影响
              const utcDate = new Date(date);
              return new Intl.DateTimeFormat('en-US', { 
                weekday: 'short',
                year: 'numeric',
                month: 'short', 
                day: 'numeric',
                timeZone: 'UTC'
              }).format(utcDate);
            }).join(', ')}
          </span>
        </div>

        <div className="flex items-center text-sm text-gray-600">
          <Clock className="mr-2" size={16} />
          <span className="font-medium">Time Range:</span>
          <span className="ml-2">
            {formatTime(request.start_time)} - {formatTime(request.end_time)}
          </span>
        </div>

        <div className="flex items-center text-sm text-gray-600">
          <Users className="mr-2" size={16} />
          <span className="font-medium">Players:</span>
          <span className="ml-2">{request.number_of_players === 0 ? 'Any' : request.number_of_players}</span>
        </div>

        <div className="flex items-center text-sm text-gray-600">
          <Mail className="mr-2" size={16} />
          <span className="font-medium">Notifications:</span>
          <span className="ml-2 capitalize">
            {request.notification_method}
            <span className="text-gray-500 normal-case">
              {request.notification_method === 'email' && ` (${request.contact_email})`}
              {request.notification_method === 'sms' && ` (${request.contact_phone})`}
              {request.notification_method === 'both' && 
                ` (${request.contact_email}${request.contact_phone ? `, ${request.contact_phone}` : ''})`}
            </span>
          </span>
        </div>

        {request.is_active && (
          <div className="text-sm text-gray-500 pt-2 border-t border-gray-100">
            <span className="font-medium">Expires:</span> {' '}
            {new Date(request.expires_at).toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default UserDashboard;